"use client";

import React, { Fragment } from "react";
import NextLink from "@/components/interactive/NextLink";

interface MdxRendererProps {
  content: string;
  noSpacing?: boolean;
}

export default function MdxRenderer({
  content,
  noSpacing = false,
}: MdxRendererProps) {
  const parseInlineMarkdown = (
    text: string,
  ): (string | React.JSX.Element)[] => {
    const elements: (string | React.JSX.Element)[] = [];
    let lastIndex = 0;

    // Match patterns: **bold**, *italic*, `inline code`
    const regex = /(\*\*(.*?)\*\*|\*(.*?)\*|`(.*?)`|\[(.*?)\]\((.*?)\))/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const [
        fullMatch,
        mdMatch,
        boldText,
        italicText,
        inlineCode,
        linkText,
        linkUrl,
      ] = match;

      if (match.index > lastIndex) {
        elements.push(text.slice(lastIndex, match.index));
      }

      if (mdMatch) {
        if (italicText) {
          elements.push(<em key={match.index}>{italicText}</em>);
        } else if (inlineCode) {
          elements.push(
            <code key={match.index} className="bg-slate px-1 rounded-small">
              {inlineCode}
            </code>,
          );
        } else if (linkUrl) {
          elements.push(
            <NextLink key={match.index} href={linkUrl}>
              {linkText || linkUrl}
            </NextLink>,
          );
        } else {
          elements.push(<strong key={match.index}>{boldText}</strong>);
        }
      }

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex));
    }

    return elements;
  };

  const parseMarkdown = (markdown: string): React.JSX.Element[] => {
    const paragraphs = markdown.split(/\n\s*\n/);

    return paragraphs.map((paragraph, index) => {
      const lines = paragraph.split("\n").map((line, lineIndex) => {
        // Headings (###, ##, #)
        if (/^###\s/.test(line)) {
          return (
            <h3 key={lineIndex} className="text-lg font-bold">
              {parseInlineMarkdown(line.replace(/^###\s/, ""))}
            </h3>
          );
        } else if (/^##\s/.test(line)) {
          return (
            <h2 key={lineIndex} className="text-xl font-semibold">
              {parseInlineMarkdown(line.replace(/^##\s/, ""))}
            </h2>
          );
        } else if (/^#\s/.test(line)) {
          return (
            <h1 key={lineIndex} className="text-2xl font-bold">
              {parseInlineMarkdown(line.replace(/^#\s/, ""))}
            </h1>
          );
        }

        // Lists (- item, * item)
        if (/^\s*[-*]\s+/.test(line)) {
          return (
            <li key={lineIndex} className="ml-5 list-disc">
              {parseInlineMarkdown(line.replace(/^\s*[-*]\s+/, ""))}
            </li>
          );
        }

        // Numbered lists (1. item)
        if (/^\s*\d+\.\s+/.test(line)) {
          return (
            <li key={lineIndex} className="ml-5 list-decimal">
              {parseInlineMarkdown(line.replace(/^\s*\d+\.\s+/, ""))}
            </li>
          );
        }

        // Code blocks (```)
        if (/^```/.test(line)) {
          return (
            <pre key={lineIndex} className="language-bash" tabIndex={0}>
              <code data-prismjs-copy="Copy All" className="language-bash">
                {line.replace(/^```/, "")}
              </code>
            </pre>
          );
        }

        return lineIndex > 0
          ? [<br key={`br-${lineIndex}`} />, ...parseInlineMarkdown(line)]
          : parseInlineMarkdown(line);
      });

      if (
        lines.some((line: any) => line?.props?.className?.includes("list-disc"))
      )
        return (
          <ul key={index} className={noSpacing ? "mb-0" : "mb-4"}>
            {lines}
          </ul>
        );

      if (
        lines.some((line: any) =>
          line?.props?.className?.includes("list-decimal"),
        )
      )
        return (
          <ol key={index} className={noSpacing ? "mb-0" : "mb-4"}>
            {lines}
          </ol>
        );

      if (lines.some((line: any) => line?.type === "pre"))
        return <Fragment key={index}>{lines}</Fragment>;

      return (
        <p key={index} className={noSpacing ? "mb-0" : "mb-4"}>
          {lines}
        </p>
      );
    });
  };

  return <div className="prose">{parseMarkdown(content)}</div>;
}
